import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    Save, RefreshCw, FileText, CheckCircle, AlertCircle, Plus, Trash2, Edit,
    Search, Filter, ExternalLink, File, FileType, Video, Link as LinkIcon, X
} from 'lucide-react';
import { API_CONFIG } from '../../config/api.config';

// Interface for Resource Item
interface ResourceItem {
    id: string;
    title: string;
    description: string;
    link: string;
    type: 'pdf' | 'doc' | 'ppt' | 'video' | 'link';
    grade: string | number;
    category: string;
    targetAudience: string;
    dateAdded?: string;
}

const ManageResources: React.FC = () => {
    const { user } = useAuth();
    const [resources, setResources] = useState<ResourceItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<ResourceItem | null>(null);
    const [formData, setFormData] = useState<ResourceItem>({
        id: '',
        title: '',
        description: '',
        link: '',
        type: 'pdf',
        grade: '10',
        category: 'Sách',
        targetAudience: 'Học sinh'
    });

    // Fetch Resources
    const fetchResources = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            // Fetch via Backend API to avoid CORS
            const res = await fetch(`${API_CONFIG.python.apiUrl}/site-admin/resources/list`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            setResources(data.resources || []);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: 'Không thể tải dữ liệu.' });
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, []);

    // Handle Form Change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Open Modal
    const openModal = (item?: ResourceItem) => {
        if (item) {
            setEditingItem(item);
            setFormData(item);
        } else {
            setEditingItem(null);
            setFormData({
                id: Date.now().toString(),
                title: '',
                description: '',
                link: '',
                type: 'pdf',
                grade: '10',
                category: 'Sách',
                targetAudience: 'Học sinh'
            });
        }
        setIsModalOpen(true);
    };

    // Save Item (Add or Edit)
    const handleSaveItem = () => {
        if (!formData.title || !formData.link) {
            alert('Vui lòng nhập tiêu đề và đường dẫn file!');
            return;
        }

        let updatedResources = [...resources];
        if (editingItem) {
            // Update existing
            updatedResources = updatedResources.map(r => r.id === editingItem.id ? formData : r);
        } else {
            // Add new
            updatedResources.unshift({ ...formData, id: Date.now().toString() });
        }

        setResources(updatedResources);
        setIsModalOpen(false);
        saveToBackend(updatedResources);
    };

    // Delete Item
    const handleDelete = (id: string) => {
        if (confirm('Bạn có chắc muốn xóa tài liệu này?')) {
            const updatedResources = resources.filter(r => r.id !== id);
            setResources(updatedResources);
            saveToBackend(updatedResources);
        }
    };

    // Save to Backend API
    const saveToBackend = async (data: ResourceItem[]) => {
        setSaving(true);
        setMessage(null);
        try {
            const token = localStorage.getItem('token');
            // Wrap in object structure expected by resources.json
            const payload = {
                lastUpdated: new Date().toISOString(),
                resources: data
            };

            const response = await fetch(`${API_CONFIG.python.apiUrl}/site-admin/resources/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to save');

            setMessage({ type: 'success', text: 'Đã lưu thay đổi thành công!' });

            // Clear success message after 3s
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: 'Lỗi khi lưu dữ liệu lên server.' });
        } finally {
            setSaving(false);
        }
    };

    // Helper for Icons
    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
            case 'doc': return <File className="w-5 h-5 text-blue-500" />;
            case 'ppt': return <FileType className="w-5 h-5 text-orange-500" />;
            case 'video': return <Video className="w-5 h-5 text-purple-500" />;
            default: return <LinkIcon className="w-5 h-5 text-gray-500" />;
        }
    };

    if (!user || user.role !== 'admin') {
        return <div className="p-8 text-center text-red-500">Bạn không có quyền truy cập.</div>;
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <FileText className="w-8 h-8 text-blue-600" />
                        Quản lý Tài liệu VatLi365.vn
                    </h1>
                    <p className="text-gray-500 mt-1">Đăng tài liệu Word, PDF, PowerPoint lên trang chủ</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchResources}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        title="Tải lại"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    <button
                        onClick={() => openModal()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        Đăng tài liệu mới
                    </button>
                </div>
            </div>

            {/* Status Message */}
            {message && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                    {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    {message.text}
                </div>
            )}

            {/* Main Content - List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 font-semibold text-gray-700 text-sm w-12">#</th>
                                <th className="px-6 py-4 font-semibold text-gray-700 text-sm">Tiêu đề tài liệu</th>
                                <th className="px-6 py-4 font-semibold text-gray-700 text-sm w-32">Loại file</th>
                                <th className="px-6 py-4 font-semibold text-gray-700 text-sm w-24">Lớp</th>
                                <th className="px-6 py-4 font-semibold text-gray-700 text-sm w-32">Danh mục</th>
                                <th className="px-6 py-4 font-semibold text-gray-700 text-sm w-40">Đối tượng</th>
                                <th className="px-6 py-4 font-semibold text-gray-700 text-sm text-right w-32">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {resources.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                                        Chưa có tài liệu nào. Hãy bấm "Đăng tài liệu mới" để bắt đầu.
                                    </td>
                                </tr>
                            ) : (
                                resources.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 text-gray-400 text-sm">{index + 1}</td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{item.title}</div>
                                            <div className="text-xs text-gray-500 truncate max-w-xs">{item.description}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-sm text-gray-600 capitalize">
                                                {getTypeIcon(item.type)}
                                                {item.type.toUpperCase()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.grade.toString() === '10' ? 'bg-blue-100 text-blue-800' :
                                                item.grade.toString() === '11' ? 'bg-green-100 text-green-800' :
                                                    'bg-purple-100 text-purple-800'
                                                }`}>
                                                Lớp {item.grade}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{item.category}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{item.targetAudience}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => openModal(item)}
                                                    className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                                <a
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Form */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingItem ? 'Chỉnh sửa tài liệu' : 'Đăng tài liệu mới'}
                            </h2>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 p-1"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Link Input - Most Important */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Link Tài liệu (Google Drive / Link trực tiếp) <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <LinkIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                    <input
                                        type="url"
                                        name="link"
                                        value={formData.link}
                                        onChange={handleInputChange}
                                        placeholder="https://drive.google.com/file/d/..."
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    />
                                </div>
                                <p className="mt-1.5 text-xs text-gray-500">
                                    💡 Mẹo: Upload file lên Google Drive, sau đó chọn "Chia sẻ" &rarr; Copy link và dán vào đây.
                                </p>
                            </div>

                            {/* Title & Type */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tiêu đề <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Ví dụ: Đề thi thử Vật lý 12 - Tháng 10"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Loại file</label>
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    >
                                        <option value="pdf">PDF</option>
                                        <option value="doc">Word (.doc/docx)</option>
                                        <option value="ppt">PowerPoint</option>
                                        <option value="video">Video</option>
                                        <option value="link">Link khác</option>
                                    </select>
                                </div>
                            </div>

                            {/* Grade & Audience */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Khối lớp</label>
                                    <select
                                        name="grade"
                                        value={formData.grade}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    >
                                        <option value="10">Lớp 10</option>
                                        <option value="11">Lớp 11</option>
                                        <option value="12">Lớp 12</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Danh mục</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    >
                                        <option value="Sách">Sách / Giáo trình</option>
                                        <option value="Giáo án">Giáo án</option>
                                        <option value="Đề thi">Đề thi / Kiểm tra</option>
                                        <option value="Công thức">Công thức / Lý thuyết</option>
                                        <option value="Bài giảng">Bài giảng</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Đối tượng</label>
                                    <select
                                        name="targetAudience"
                                        value={formData.targetAudience}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    >
                                        <option value="Học sinh">Học sinh</option>
                                        <option value="Giáo viên">Giáo viên</option>
                                        <option value="Tất cả">Tất cả</option>
                                    </select>
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Mô tả ngắn</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={3}
                                    placeholder="Mô tả nội dung tài liệu..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                />
                            </div>

                        </div>

                        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                                Hủy bỏ
                            </button>
                            <button
                                onClick={handleSaveItem}
                                className="px-6 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md transition-transform active:scale-95"
                            >
                                {editingItem ? 'Lưu thay đổi' : 'Đăng ngay'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ManageResources;
